import { useState } from 'react';
import { Button, Card, Alert } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  useGetUsersBySecurityGroupQuery,
  useGetAvailableUsersForSecurityGroupQuery,
  useAssignUsersToSecurityGroupMutation
} from '../../services/portal';
import UserSelectionModal from './UserSelectionModal';
import LoadingState from './LoadingState';

/**
 * Displays and manages users assigned to a security group.
 * Provides search, list view, and a modal to add/remove users in bulk.
 * - securityGroupId {string|number}: ID of the security group whose users are managed.
 * - organizationId {string|number}: Organization ID used to fetch available users.
 * @returns {JSX.Element} Card with users table and a modal to manage assignments.
 */
const GroupUsers = ({ securityGroupId, organizationId }) => {
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: groupUsers,
    isLoading: isLoadingUsers,
    refetch: refetchUsers
  } = useGetUsersBySecurityGroupQuery(securityGroupId);

  const {
    data: availableUsers
  } = useGetAvailableUsersForSecurityGroupQuery({
    securityGroupId,
    organizationId
  });

  const [assignUsers] = useAssignUsersToSecurityGroupMutation();

    /**
     * Opens the user management modal and seeds the selection from users currently assigned to the group.
     * @returns {void}
     */
    const handleManageUsers = () => {
    setSelectedUsers(groupUsers?.users?.map(user => user.UserID) || []);
    setShowUserModal(true);
  };

    /**
     * Persists selected user IDs to the backend and refreshes the list.
     * @param {number[]} userIds - Array of user IDs to assign to the group.
     * @returns {Promise<void>}
     */
    const handleSaveUsers = async (userIds) => {
    try {
      await assignUsers({
        securityGroupId: parseInt(securityGroupId),
        user_ids: userIds
      }).unwrap();

      refetchUsers();
      setShowUserModal(false);
    } catch (error) {
      console.error('Failed to assign users:', error);
    }
  };

    /**
     * Renders the date a user was added to the group in a readable format.
     * @param {Object} rowData - The user row object.
     * @param {string|number|Date} rowData.AddedToGroupDate - Date the user was added.
     * @returns {JSX.Element} A span with a localized date string.
     */
    const userDateTemplate = (rowData) => {
    return (
      <span className="text-muted">
        {new Date(rowData.AddedToGroupDate).toLocaleDateString()}
      </span>
    );
  };

  const users = groupUsers?.users || [];
  const available = availableUsers?.users || [];

    /**
     * Handles changes to the search input used to filter the visible users.
     * @param {React.ChangeEvent<HTMLInputElement>} e - Input change event.
     * @returns {void}
     */
    const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

    /**
     * Filters the group's users by the current search term.
     * Matches on any string field within a user row.
     * @returns {Object[]} The filtered list of users.
     */
    const filterUsers = () => {
    if (!users || !users.length) return [];
    return users.filter(user => {
      return Object.values(user).some(value => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      });
    });
  };

  const filteredUsers = filterUsers();

  if (isLoadingUsers) {
    return (
      <LoadingState 
        title="Group Users" 
        icon="fas fa-users"
        message="Loading group users..." 
      />
    );
  }

  return (
    <div>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Group Members</h5>
          <Button
            variant="primary"
            className="btn2"
            onClick={handleManageUsers}
          >
            Manage Users
          </Button>
        </Card.Header>
        <Card.Body>
          {users.length === 0 ? (
            <div className="card">
              <div className="card-body">
                <div className="text-center">
                  <i className="fas fa-users fa-3x text-muted mb-3"></i>
                  <h5>No Users Assigned</h5>
                  <p className="text-muted">
                    This security group doesn't have any users yet. Click "Manage Users" to assign users to this group.
                  </p>
                  <Button variant="primary" className="btn2" onClick={handleManageUsers}>
                    Assign Users
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="datatable-custom">
              <div className="m-0 justify-content-end d-flex align-items-end p-3">
                <input
                  type="text"
                  className="m-0 col-2 card d-flex align-items-end p-2"
                  placeholder="Type to Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <span className="p-inputgroup-addon">
                  <i className="pi pi-search"></i>
                </span>
              </div>
              <DataTable
                value={filteredUsers}
                removableSort
                tableClassName="table dataTable no-footer table-striped"
                emptyMessage="No users found"
              >
                <Column
                  field="FirstName"
                  header="First Name"
                  sortable
                />
                <Column
                  field="LastName"
                  header="Last Name"
                  sortable
                />
                <Column
                  field="UserEmail"
                  header="Email"
                  sortable
                />
                <Column
                  field="Company"
                  header="Company"
                  sortable
                />
                <Column
                  field="Department"
                  header="Department"
                  sortable
                />
                <Column
                  body={userDateTemplate}
                  header="Date Added"
                  style={{ width: '120px' }}
                />
              </DataTable>
            </div>
          )}
        </Card.Body>
      </Card>

      <UserSelectionModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        groupUsers={users}
        availableUsers={available}
        selectedUsers={selectedUsers}
        onSelectionChange={setSelectedUsers}
        onSave={handleSaveUsers}
      />
    </div>
  );
};

export default GroupUsers;
